import React, { useState, useId } from 'react'
import type { ReviewerDisplayState } from './inboxTypes'
import { createInboxMessage } from './services/createInboxMessage'
import { ShareWithSupplierCTA } from './ShareWithSupplierCTA'

export type WriteMessageFormSendResult = 'sent' | 'keep_draft'
type WriteMessageFormOnSendResult = WriteMessageFormSendResult | undefined
type WriteMessageFormOnSend = (
  text: string
) => WriteMessageFormOnSendResult | Promise<WriteMessageFormOnSendResult>

export interface WriteMessageFormProps {
  conversationId?: number
  reviewerDisplayState: ReviewerDisplayState
  disableSend?: boolean
  contentAfterTextarea?: React.ReactNode
  onSend?: WriteMessageFormOnSend
  onCancel?: () => void
  showShareWithSupplierCTA?: boolean
  reviewAuthorName?: string
}

export const WriteMessageForm: React.FC<WriteMessageFormProps> = ({
  conversationId,
  reviewerDisplayState,
  disableSend = false,
  contentAfterTextarea,
  onSend,
  onCancel,
  showShareWithSupplierCTA = false,
  reviewAuthorName
}) => {
  const [body, setBody] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)

  const uniqueId = useId()
  const textareaId = `write-message-textarea-${uniqueId}`
  const anonymousAlertId = `write-message-anonymous-alert-${uniqueId}`

  const trimmedBody = body.trim()
  const hasText = trimmedBody.length > 0
  const isSendDisabled = !hasText || isSending || disableSend

  const handleSend = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault()
    if (isSendDisabled) return

    try {
      setIsSending(true)
      setSendError(null)

      if (conversationId != null) {
        await createInboxMessage(conversationId, trimmedBody)
        setBody('')
        try {
          if (onSend != null) await onSend(trimmedBody)
        } catch {
          // Message send already succeeded, ignore post send callback errors
        }
      } else if (onSend != null) {
        const result = await onSend(trimmedBody)
        if (result !== 'keep_draft') {
          setBody('')
        }
      }
    } catch {
      setSendError('Unable to send message. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    setBody('')
    onCancel?.()
  }

  const renderAnonymousAlert = (): React.ReactNode => {
    if (reviewerDisplayState === 'ALL_VISIBLE') return null

    switch (reviewerDisplayState) {
      case 'RECIPIENT_ANONYMOUS':
        return (
          <div id={anonymousAlertId} className='write-message-info-note' role='status'>
            <i className='fa-solid fa-circle-info' aria-hidden='true' />
            <strong>Note:</strong> Since this review is anonymous, you will not be able to see this user's name.
          </div>
        )

      case 'SENDER_ANONYMOUS':
        return (
          <div
            id={anonymousAlertId}
            className='write-message-info-note'
            role='status'
          >
            <i className='fa-solid fa-circle-info' aria-hidden='true' />
            <strong>Note:</strong> Since your review is anonymous, this person will not be able to see your name unless you disclose it.
          </div>
        )

      default:
        return null
    }
  }

  return (
    <>
      {showShareWithSupplierCTA && (
        <ShareWithSupplierCTA
          reviewAuthorName={reviewAuthorName ?? ''}
          isRecipientAnonymous={reviewerDisplayState === 'RECIPIENT_ANONYMOUS'}
          setBody={setBody}
        />
      )}
      <form
        id='inbox-write-message-form'
        className='write-message-form'
        onSubmit={(event) => {
          void handleSend(event)
        }}
      >
        {/* Invisible label for accessibility */}
        <label
          htmlFor={textareaId}
          className='sr-only'
        >
          Message
        </label>

        <textarea
          id={textareaId}
          className='write-message-textarea'
          placeholder='Write your message here'
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={isSending}
          aria-describedby={
            reviewerDisplayState === 'ALL_VISIBLE' ? undefined : anonymousAlertId
          }
        />

        {contentAfterTextarea}

        <div className='write-message-actions'>
          {sendError != null && (
            <div className='write-message-error' role='alert'>
              {sendError}
            </div>
          )}

          {renderAnonymousAlert()}

          <div className='write-message-actions-buttons'>
            <button
              type='button'
              className='write-message-cancel'
              onClick={handleCancel}
            >
              Cancel
            </button>

            <button
              type='submit'
              className={`write-message-send${isSendDisabled ? ' write-message-send--disabled' : ''}`}
              disabled={isSendDisabled}
            >
              Send
            </button>
          </div>
        </div>
      </form>
    </>
  )
}
